/**
 * @Author: Q in
 * @Date: 2026-03-11 15:00:00
 * @LastEditors: Q in
 * @LastEditTime: 2026-05-15 15:00:00
 * @FilePath: /new_kefu_pc/src/utils/index-websocket.js
 * @Description: 客户端 WebSocket 服务 - 支持自动重连、心跳检测、消息分发、状态监听、休眠恢复
 */

// 配置常量
const CONFIG = {
	MAX_RECONNECT_COUNT: 5,
	RECONNECT_DELAY: 3000,
	HEARTBEAT_DELAY: 28000, // 心跳间隔（28秒）
	HEARTBEAT_TIMEOUT: 28000, // 心跳超时时间（多久没收到响应认为断开）
	HEARTBEAT_MESSAGE: { a: 'ping', c: 'Message' },
	HEARTBEAT_RESPONSE: 'pong', // 服务端心跳响应标识
	VISIBILITY_RECONNECT_DELAY: 500, // 页面恢复可见后的重连延迟
};

class SimpleWebSocket {
	/**
	 * @param {string} url WebSocket 连接地址
	 */
	constructor(url) {
		this.url = url;
		this.ws = null;
		this.connected = false;
		this.isConnecting = false;
		this.reconnectCount = 0;
		this.maxReconnectCount = CONFIG.MAX_RECONNECT_COUNT;
		this.reconnectDelay = CONFIG.RECONNECT_DELAY;
		this.heartbeatTimer = null;
		this.heartbeatTimeoutTimer = null; // 心跳超时检测定时器
		this.reconnectTimer = null;
		this.isManualClose = false;
		this.lastHeartbeatResponseTime = 0; // 最后一次收到心跳响应的时间
		this.visibilityChangeHandler = null; // 页面可见性变化处理函数引用

		// 使用 Map<type, Set<handler>> 管理消息处理器
		this.messageHandlers = new Map();
		// 使用 Set 管理状态变化监听器
		this.statusChangeHandlers = new Set();

		// 绑定页面可见性变化监听
		this._bindVisibilityListener();
	}

	// ─── 页面可见性监听 ──────────────────────────────────────────────────────

	/**
	 * 绑定页面可见性变化监听
	 * 当标签页从后台恢复时，检测连接状态并决定是否重连
	 */
	_bindVisibilityListener() {
		// 清理旧的监听器
		if (this.visibilityChangeHandler) {
			document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
		}

		this.visibilityChangeHandler = () => {
			if (!document.hidden) {
				// 页面从隐藏变为可见
				console.log('[WS] 页面恢复可见，检测连接状态');
				this._handleVisibilityRestore();
			} else {
				// 页面变为隐藏，记录时间
				console.log('[WS] 页面进入后台');
				this.lastHeartbeatResponseTime = Date.now();
			}
		};

		document.addEventListener('visibilitychange', this.visibilityChangeHandler);

		// 也监听页面冻结/恢复事件（某些浏览器支持）
		if ('onfreeze' in document) {
			document.addEventListener('freeze', () => {
				console.log('[WS] 页面冻结');
				this.lastHeartbeatResponseTime = Date.now();
			});

			document.addEventListener('resume', () => {
				console.log('[WS] 页面恢复');
				this._handleVisibilityRestore();
			});
		}

		// 监听网络状态变化
		window.addEventListener('online', () => {
			console.log('[WS] 网络恢复在线');
			if (!this.connected && !this.isConnecting && !this.isManualClose) {
				this.connect();
			}
		});

		window.addEventListener('offline', () => {
			console.log('[WS] 网络离线');
			this._stopHeartbeat();
		});
	}

	/**
	 * 处理页面恢复可见时的连接检测
	 */
	_handleVisibilityRestore() {
		// 清除之前的重连定时器，避免冲突
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}

		// 检查连接状态
		if (!this.connected || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
			console.log('[WS] 页面恢复后检测到连接断开，尝试重连');
			// 短暂延迟后重连，给浏览器恢复时间
			setTimeout(() => {
				if (!this.connected && !this.isConnecting && !this.isManualClose) {
					// 重置重连计数，给重连更多机会
					if (this.reconnectCount >= this.maxReconnectCount) {
						this.reconnectCount = 0;
					}
					this.connect();
				}
			}, CONFIG.VISIBILITY_RECONNECT_DELAY);
		} else {
			// 连接正常，但可能心跳已失效，立即发送一次心跳验证
			console.log('[WS] 页面恢复，连接存在，发送验证心跳');
			this.lastHeartbeatResponseTime = Date.now();
			this.send(CONFIG.HEARTBEAT_MESSAGE);
			this._resetHeartbeatTimeout();
		}
	}

	// ─── 连接管理 ────────────────────────────────────────────────────────────

	/** 发起 WebSocket 连接 */
	connect() {
		if (!window.WebSocket) {
			console.error('[WS] 浏览器不支持 WebSocket');
			return;
		}

		// 检查网络状态
		if (!navigator.onLine) {
			console.log('[WS] 网络离线，延迟连接');
			return;
		}

		// 防止重复连接
		if (this.connected || this.isConnecting) {
			console.log('[WS] 已连接或正在连接中，跳过重复连接');
			return;
		}

		// 清除之前的重连定时器
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}

		try {
			this.isConnecting = true;
			this.ws = new WebSocket(this.url);
			this.ws.onopen = this._handleOpen.bind(this);
			this.ws.onmessage = this._handleMessage.bind(this);
			this.ws.onclose = this._handleClose.bind(this);
			this.ws.onerror = this._handleError.bind(this);
		} catch (error) {
			console.error('[WS] 创建连接异常:', error);
			this.isConnecting = false;
			this._scheduleReconnect();
		}
	}

	/** 主动关闭连接（不触发重连） */
	close() {
		console.log('[WS] 主动关闭连接');
		this.isManualClose = true;
		this.isConnecting = false;
		this._stopHeartbeat();
		this._clearHeartbeatTimeout();
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}
		if (this.ws) {
			// 移除事件监听，避免触发重连
			this.ws.onclose = null;
			this.ws.onerror = null;
			this.ws.close();
			this.ws = null;
		}
		this.connected = false;
		this.reconnectCount = 0;
	}

	/**
	 * 销毁实例，清理所有资源
	 */
	destroy() {
		this.close();
		// 清理页面可见性监听
		if (this.visibilityChangeHandler) {
			document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
			this.visibilityChangeHandler = null;
		}
		// 清理所有消息处理器
		this.messageHandlers.clear();
		// 清理所有状态监听器
		this.statusChangeHandlers.clear();
	}

	/**
	 * 更新 WebSocket URL 并重新连接
	 * @param {string} newUrl 新的连接地址
	 * @param {boolean} autoConnect 是否自动重连，默认 true
	 */
	updateUrl(newUrl, autoConnect = true) {
		if (!newUrl) {
			console.error('[WS] URL 不能为空');
			return;
		}

		console.log('[WS] 更新 URL:', newUrl);
		this.url = newUrl;

		if (autoConnect) {
			this.reconnect();
		}
	}

	/**
	 * 重新连接（先关闭再连接）
	 */
	reconnect() {
		console.log('[WS] 手动重新连接');
		const oldManualClose = this.isManualClose;
		this.isManualClose = false;

		// 关闭现有连接
		if (this.ws) {
			this.ws.onclose = null;
			this.ws.onerror = null;
			this.ws.close();
			this.ws = null;
		}
		this.connected = false;
		this.isConnecting = false;

		// 重置重连计数
		this.reconnectCount = 0;

		// 开始新连接
		this.connect();

		// 恢复原状态
		this.isManualClose = oldManualClose;
	}

	// ─── 消息收发 ────────────────────────────────────────────────────────────

	/**
	 * 发送消息
	 * @param {Object|string} data
	 * @returns {boolean} 是否发送成功
	 */
	send(data) {
		if (!this.connected || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
			console.warn('[WS] 未连接，无法发送消息');
			return false;
		}
		try {
			this.ws.send(typeof data === 'object' ? JSON.stringify(data) : data);
			return true;
		} catch (error) {
			console.error('[WS] 发送失败:', error);
			return false;
		}
	}

	/**
	 * 注册消息处理器
	 * @param {string}   type    消息类型（'default' 表示所有消息）
	 * @param {Function} handler 处理函数
	 */
	on(type, handler) {
		if (!this.messageHandlers.has(type)) {
			this.messageHandlers.set(type, new Set());
		}
		this.messageHandlers.get(type).add(handler);
	}

	/**
	 * 移除消息处理器
	 * @param {string}   type
	 * @param {Function} handler
	 */
	off(type, handler) {
		const handlers = this.messageHandlers.get(type);
		if (handlers) {
			handlers.delete(handler);
			if (handlers.size === 0) this.messageHandlers.delete(type);
		}
	}

	/**
	 * 注册连接状态变化监听器
	 * @param {Function} handler (isConnected: boolean) => void
	 * @returns {Function} 取消注册的函数
	 */
	onStatusChange(handler) {
		this.statusChangeHandlers.add(handler);
		return () => this.statusChangeHandlers.delete(handler);
	}

	// ─── 私有方法 ────────────────────────────────────────────────────────────

	_handleOpen() {
		console.log('[WS] 连接成功');
		this.connected = true;
		this.isConnecting = false;
		this.reconnectCount = 0;
		this.isManualClose = false;
		this.lastHeartbeatResponseTime = Date.now();
		this._startHeartbeat();
		this._emitStatusChange(true);
	}

	_handleClose(event) {
		console.log('[WS] 连接关闭, code:', event.code, ', reason:', event.reason);
		this.connected = false;
		this.isConnecting = false;
		this._stopHeartbeat();
		this._clearHeartbeatTimeout();
		this._emitStatusChange(false);
		// 非主动关闭才重连
		if (!this.isManualClose) {
			this._scheduleReconnect();
		}
	}

	_handleError(error) {
		console.error('[WS] 连接错误:', error);
		this.connected = false;
		this.isConnecting = false;
		this._stopHeartbeat();
		this._clearHeartbeatTimeout();
		this._emitStatusChange(false);
		// 错误后也尝试重连
		if (!this.isManualClose) {
			this._scheduleReconnect();
		}
	}

	_handleMessage(event) {
		try {
			const message = JSON.parse(event.data);

			// 检测心跳响应（根据实际服务端响应格式调整）
			if (message.a === 'pong' || message.type === 'pong' || message.msg === 'pong') {
				this.lastHeartbeatResponseTime = Date.now();
				this._resetHeartbeatTimeout();
				return; // 心跳响应不需要分发给业务处理器
			}

			const type = message.type || 'default';
			const handlers = this.messageHandlers.get(type);
			if (handlers && handlers.size > 0) {
				handlers.forEach((handler) => {
					try {
						handler(message);
					} catch (e) {
						console.error('[WS] 消息处理异常:', e);
					}
				});
			} else {
				// 如果没有特定类型的处理器，尝试使用 default
				const defaultHandlers = this.messageHandlers.get('default');
				if (defaultHandlers && defaultHandlers.size > 0) {
					defaultHandlers.forEach((handler) => {
						try {
							handler(message);
						} catch (e) {
							console.error('[WS] 消息处理异常:', e);
						}
					});
				}
			}
		} catch (error) {
			console.error('[WS] 消息解析失败:', error);
		}
	}

	_scheduleReconnect() {
		// 检查网络状态
		if (!navigator.onLine) {
			console.log('[WS] 网络离线，暂停重连');
			return;
		}

		if (this.reconnectCount >= this.maxReconnectCount) {
			console.error('[WS] 已达最大重连次数，停止重连');
			return;
		}
		this.reconnectCount++;
		const delay = this.reconnectDelay * Math.min(this.reconnectCount, 3);
		console.log(`[WS] 第 ${this.reconnectCount}/${this.maxReconnectCount} 次重连，${delay}ms 后重试`);
		this.reconnectTimer = setTimeout(() => this.connect(), delay);
	}

	// ─── 心跳管理（增强版） ──────────────────────────────────────────────────

	_startHeartbeat() {
		this._stopHeartbeat();
		this.lastHeartbeatResponseTime = Date.now();
		this.heartbeatTimer = setInterval(() => {
			if (this.connected && this.ws && this.ws.readyState === WebSocket.OPEN) {
				this.send(CONFIG.HEARTBEAT_MESSAGE);
				// 启动心跳超时检测
				this._startHeartbeatTimeout();
			}
		}, CONFIG.HEARTBEAT_DELAY);
	}

	_stopHeartbeat() {
		if (this.heartbeatTimer) {
			clearInterval(this.heartbeatTimer);
			this.heartbeatTimer = null;
		}
	}

	/**
	 * 启动心跳超时检测
	 * 如果 HEARTBEAT_TIMEOUT 时间内没收到心跳响应，认为连接已断开
	 */
	_startHeartbeatTimeout() {
		this._clearHeartbeatTimeout();
		this.heartbeatTimeoutTimer = setTimeout(() => {
			const timeSinceLastResponse = Date.now() - this.lastHeartbeatResponseTime;
			if (timeSinceLastResponse >= CONFIG.HEARTBEAT_TIMEOUT) {
				console.warn('[WS] 心跳超时，超过', timeSinceLastResponse, 'ms未收到响应，主动断开重连');
				this.connected = false;
				this._emitStatusChange(false);
				// 主动关闭当前连接，触发重连
				if (this.ws) {
					this.ws.onclose = null; // 避免触发额外的重连
					this.ws.close();
					this.ws = null;
				}
				this._stopHeartbeat();
				this._scheduleReconnect();
			} else {
				// 如果还没超时，重新设置定时器
				this._startHeartbeatTimeout();
			}
		}, CONFIG.HEARTBEAT_TIMEOUT);
	}

	/**
	 * 重置心跳超时检测（收到心跳响应时调用）
	 */
	_resetHeartbeatTimeout() {
		this._clearHeartbeatTimeout();
		if (this.connected) {
			this._startHeartbeatTimeout();
		}
	}

	/**
	 * 清除心跳超时检测
	 */
	_clearHeartbeatTimeout() {
		if (this.heartbeatTimeoutTimer) {
			clearTimeout(this.heartbeatTimeoutTimer);
			this.heartbeatTimeoutTimer = null;
		}
	}

	_emitStatusChange(isConnected) {
		this.statusChangeHandlers.forEach((handler) => {
			try {
				handler(isConnected);
			} catch (e) {
				console.error('[WS] 状态处理异常:', e);
			}
		});
	}
}

// ─── 单例管理 ────────────────────────────────────────────────────────────────

const wsInstances = {};

/**
 * 创建或获取 WebSocket 单例
 * @param {string} url 仅首次调用有效
 * @param {string} moduleName 模块名称
 * @returns {SimpleWebSocket}
 */
export function createWebSocket(url, moduleName = 'default') {
	if (!wsInstances[moduleName] && url) {
		wsInstances[moduleName] = new SimpleWebSocket(url);
	}
	return wsInstances[moduleName];
}

/**
 * 获取当前 WebSocket 实例
 * @param {string} moduleName 模块名称
 * @returns {SimpleWebSocket|null}
 */
export function getWebSocketInstance(moduleName = 'default') {
	return wsInstances[moduleName] || null;
}

/**
 * 重置 WebSocket 单例
 * @param {boolean} closeConnection 是否关闭连接，默认 true
 * @param {string} moduleName 模块名称
 */
export function resetWebSocket(closeConnection = true, moduleName = 'default') {
	if (wsInstances[moduleName]) {
		if (closeConnection) {
			wsInstances[moduleName].destroy(); // 使用 destroy 清理所有资源
		}
		wsInstances[moduleName] = null;
	}
}

/**
 * 更新 WebSocket 连接
 * @param {string} newUrl 新的 WebSocket URL
 * @param {boolean} autoConnect 是否自动连接，默认 true
 * @param {string} moduleName 模块名称
 * @returns {SimpleWebSocket|null}
 */
export function updateWebSocketConnection(newUrl, autoConnect = true, moduleName = 'default') {
	if (!newUrl) {
		console.error('[WS] updateWebSocketConnection: URL 不能为空');
		return null;
	}

	if (wsInstances[moduleName]) {
		const instance = wsInstances[moduleName];
		if (instance.url === newUrl && instance.connected) {
			console.log('[WS] 已有相同 URL 的连接，跳过重新连接');
			return instance;
		}
		instance.updateUrl(newUrl, autoConnect);
	} else {
		wsInstances[moduleName] = new SimpleWebSocket(newUrl);
		if (autoConnect) {
			wsInstances[moduleName].connect();
		}
	}

	return wsInstances[moduleName];
}

export default SimpleWebSocket;
